import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, LogOut, Phone, RefreshCw, Trash2 } from "lucide-react";
import Seo from "@/components/Seo";
import Logo from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const STATUS_OPTIONS = ["new", "contacted", "won", "archived"];

export default function AdminPage() {
  const [user, setUser] = useState(undefined);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const loadLeads = async () => {
    setRefreshing(true);
    try {
      const { data } = await axios.get(`${API}/leads`, { withCredentials: true });
      setLeads(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        try {
          await axios.post(`${API}/auth/refresh`, {}, { withCredentials: true });
          const { data } = await axios.get(`${API}/leads`, { withCredentials: true });
          setLeads(data);
        } catch {
          setUser(null);
          toast.error("Session expired — please sign in again.");
        }
      } else {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail));
      }
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => setUser(r.data))
      .catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (user) loadLeads();
  }, [user]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      setUser(data);
      toast.success("Welcome back.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(null);
    setPassword("");
  };

  const removeLead = async (id) => {
    try {
      await axios.delete(`${API}/leads/${id}`, { withCredentials: true });
      setLeads((l) => l.filter((x) => x._id !== id));
      toast.success("Lead deleted.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/leads/${id}/status`, { status }, { withCredentials: true });
      setLeads((l) => l.map((x) => (x._id === id ? { ...x, status } : x)));
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  return (
    <>
      <Seo title="Admin — SAIVU" description="SAIVU admin dashboard." path="/admin" />
      <div className="min-h-screen bg-mist px-5 pb-16 pt-28 sm:pt-32" data-testid="admin-page">
        {user === undefined ? (
          <div className="flex justify-center pt-16">
            <Loader2 className="h-8 w-8 animate-spin text-navy-800" />
          </div>
        ) : user === null ? (
          <div className="flex justify-center">
            <form
              onSubmit={login}
              className="w-full max-w-md rounded-lg border border-line bg-white p-8 shadow-card sm:p-10"
              data-testid="admin-login-form"
            >
              <Logo />
              <h1 className="mt-6 text-2xl font-semibold text-navy-900">Leads dashboard</h1>
              <p className="mt-2 text-[15px] text-ink">Sign in with your admin account.</p>
              <input
                type="email"
                placeholder="Admin email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-6 w-full rounded-md border border-line bg-white px-3.5 py-2.5 text-sm text-navy-900 placeholder:text-slate-400 outline-none transition-colors focus:border-navy-800 focus:ring-2 focus:ring-navy-100"
                data-testid="admin-email-input"
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-3 w-full rounded-md border border-line bg-white px-3.5 py-2.5 text-sm text-navy-900 placeholder:text-slate-400 outline-none transition-colors focus:border-navy-800 focus:ring-2 focus:ring-navy-100"
                data-testid="admin-password-input"
              />
              <button
                type="submit"
                disabled={loading}
                className="mt-5 inline-flex w-full items-center justify-center rounded-md bg-navy-900 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-navy-800 disabled:opacity-60"
                data-testid="admin-login-button"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
              </button>
            </form>
          </div>
        ) : (
          <div className="mx-auto max-w-6xl" data-testid="admin-dashboard">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <Logo />
                <h1 className="text-2xl font-semibold text-navy-900">Leads</h1>
                <span className="rounded-md border border-line bg-white px-3 py-1 font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-navy-900">
                  {leads.length}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={loadLeads}
                  className="inline-flex items-center gap-2 rounded-md border border-line bg-white px-4 py-2 text-xs font-medium text-ink transition-colors hover:border-navy-900 hover:text-navy-900"
                  data-testid="admin-refresh-button"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} /> Refresh
                </button>
                <button
                  onClick={logout}
                  className="inline-flex items-center gap-2 rounded-md bg-navy-900 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-navy-800"
                  data-testid="admin-logout-button"
                >
                  <LogOut className="h-3.5 w-3.5" /> Sign out
                </button>
              </div>
            </div>

            {leads.length === 0 ? (
              <div className="mt-8 rounded-lg border border-line bg-white p-12 text-center" data-testid="admin-empty-state">
                <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-600" />
                <p className="mt-4 text-[15px] text-ink">No enquiries yet. New leads from the website appear here instantly.</p>
              </div>
            ) : (
              <div className="mt-8 flex flex-col gap-4" data-testid="admin-leads-list">
                {leads.map((lead) => (
                  <div
                    key={lead._id}
                    className="rounded-lg border border-line bg-white p-6 shadow-card"
                    data-testid={`admin-lead-row-${lead._id}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <div className="flex flex-wrap items-center gap-3">
                          <span className="text-lg font-semibold text-navy-900">{lead.name}</span>
                          <span className="rounded-full border border-navy-100 bg-navy-50 px-3 py-0.5 font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-navy-900">
                            {lead.service}
                          </span>
                          <select
                            value={lead.status || "new"}
                            onChange={(e) => setStatus(lead._id, e.target.value)}
                            className="rounded-md border border-line bg-white px-3 py-1 text-sm text-ink outline-none focus:border-navy-800"
                            data-testid={`admin-lead-status-${lead._id}`}
                          >
                            {STATUS_OPTIONS.map((s) => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-[15px] text-ink">
                          <a href={`tel:${lead.phone}`} className="inline-flex items-center gap-2 font-medium text-navy-900 hover:underline" data-testid={`admin-lead-phone-${lead._id}`}>
                            <Phone className="h-3.5 w-3.5 text-gold-deep" /> {lead.phone}
                          </a>
                          {lead.email && (
                            <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-2 hover:underline" data-testid={`admin-lead-email-${lead._id}`}>
                              {lead.email}
                            </a>
                          )}
                          {(lead.preferred_date || lead.preferred_slot) && (
                            <span className="inline-flex items-center text-slate-600">
                              Slot: {lead.preferred_date || "any day"} · {lead.preferred_slot || "any time"}
                            </span>
                          )}
                        </div>
                        {lead.message && <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-ink">“{lead.message}”</p>}
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <span className="font-mono text-[11px] font-medium uppercase tracking-[0.12em] text-slate-600">
                          {new Date(lead.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                        </span>
                        <button
                          onClick={() => removeLead(lead._id)}
                          className="inline-flex items-center gap-1.5 rounded-md border border-line px-3 py-1.5 text-xs text-slate-600 transition-colors hover:border-red-300 hover:text-red-600"
                          data-testid={`admin-lead-delete-${lead._id}`}
                        >
                          <Trash2 className="h-3 w-3" /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
